import React, { createContext, useContext, useState } from 'react';

const translations = {
  en: {
    // Dashboard
    dashboard: 'Dashboard',
    projects: 'Projects',
    newProject: 'New Project',
    allProjects: 'All Projects',
    activeProjects: 'Active Projects',
    completedProjects: 'Completed',
    draftProjects: 'Drafts',
    search: 'Search projects...',
    manageArchitecturalDocs: 'Manage your architectural project documentation',
    noProjectsFound: 'No projects found',
    startCreatingFirst: 'Start by creating your first project',
    loadingProjects: 'Loading projects...',
    sheets: 'sheets',
    
    // Project Card
    edit: 'Edit',
    duplicate: 'Duplicate',
    delete: 'Delete',
    export: 'Export PDF',
    lastModified: 'Last modified',
    
    // Project Types
    residential_remodel: 'Residential Remodel',
    residential_new: 'New Residential',
    tenant_improvement_food: 'Tenant Improvement - Food Service',
    tenant_improvement_retail: 'Tenant Improvement - Retail',
    tenant_improvement_office: 'Tenant Improvement - Office',
    commercial_new: 'New Commercial',
    
    // Project Type Descriptions
    residentialRemodelDesc: 'Residential remodeling and additions',
    residentialNewDesc: 'New residential construction',
    tenantFoodDesc: 'Restaurant and food service spaces',
    tenantRetailDesc: 'Retail and commercial spaces',
    tenantOfficeDesc: 'Office tenant improvements',
    commercialNewDesc: 'New commercial construction',
    
    // Status
    draft: 'Draft',
    in_progress: 'In Progress',
    completed: 'Completed',
    archived: 'Archived',
    
    // Wizard
    createNewProject: 'Create New Project',
    followSteps: 'Follow the steps to create your project documentation',
    projectType: 'Project Type',
    basicInfo: 'Basic Information',
    buildingSummary: 'Building Summary',
    projectTeam: 'Project Team',
    scopeOfWork: 'Scope of Work',
    sheetIndex: 'Sheet Index',
    schedules: 'Schedules',
    compliance: 'Compliance',
    rendersImages: 'Renders & Images',
    review: 'Review & Export',
    
    // Project Type Step
    selectProjectType: 'Select the type of project you are creating',
    pleaseSelectType: 'Please select a project type to continue',
    
    // Basic Info Step
    enterBasicInfo: 'Enter the basic project information',
    projectName: 'Project Name',
    projectNumber: 'Project Number',
    address: 'Address',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    clientOwnerName: 'Client/Owner Name',
    architectName: 'Architect Name',
    scopeOfWorkLabel: 'Scope of Work',
    
    // Placeholders
    placeholderProjectName: 'e.g., Hollywood Burbank Airport - Spring Chicken',
    placeholderProjectNumber: 'e.g., 313-23/VPMMB',
    placeholderAddress: 'e.g., 11445 Patom Dr.',
    placeholderCity: 'e.g., Culver City',
    placeholderState: 'e.g., CA',
    placeholderZip: 'e.g., 90230',
    placeholderClient: 'e.g., MCS Metropolitan Culinary Services',
    placeholderArchitect: 'e.g., Sidley Jones Architects',
    placeholderScope: 'Describe the scope of work for this project...',
    
    // Sheet Index Step
    selectSheetsToInclude: 'Select which sheets to include in your project documentation',
    intelligentPagination: 'Intelligent Auto-Pagination',
    paginationDescription: 'The system will automatically calculate how many pages each section needs. If content does not fit on one page, additional pages will be created (e.g., G0.00, G0.01, G0.02).',
    selectProjectTypeToSeeSheets: 'Select a project type to see available sheets',
    totalSheetsSelected: 'Total Sheets Selected',
    theseWillBeIncluded: 'These will be included in your documentation',
    blocks: 'blocks',
    
    // Document Preview
    documentPreview: 'Document Preview',
    autoPaginationPreview: 'Auto-pagination preview',
    startFillingDetails: 'Start filling in project details to see preview',
    totalPages: 'Total Pages',
    warnings: 'warnings',
    continued: 'continued',
    contentWillContinue: 'Content will continue on',
    full: 'full',
    
    // Buttons
    next: 'Next',
    previous: 'Previous',
    save: 'Save',
    cancel: 'Cancel',
    finish: 'Finish',
    add: 'Add',
    access: 'Access',
    getStarted: 'Get Started',
    browseLibrary: 'Browse Library',
    
    // Landing Page
    landingTagline: 'Streamline your architectural project documentation with intelligent organization and compliance management',
    quickStats: 'Quick Stats',
    comingSoon: 'Coming Soon',
    
    // Sections
    projectFiles: 'Project Files',
    projectFilesDesc: 'Manage project folders with drawings, documents, and files',
    projectFilesComingSoon: 'Project file management system with folder organization, upload/download capabilities, and document versioning.',
    
    complianceLibrary: 'Compliance Library',
    complianceLibraryDesc: 'Access building codes and regulations by state and type',
    complianceLibraryComingSoon: 'Hierarchical compliance library organized by state, construction type, and regulatory entity with intelligent suggestions.',
    
    projectBuilder: 'Project Builder',
    projectBuilderDesc: 'Create and manage architectural documentation projects',
    
    // Project Files Page
    noProjects: 'No projects yet',
    selectProject: 'Select a Project',
    selectProjectDesc: 'Choose a project from the sidebar to view and manage its files',
    searchFiles: 'Search files...',
    uploading: 'Uploading...',
    uploadFile: 'Upload File',
    download: 'Download',
    noFilesFound: 'No files found',
    noFilesYet: 'No files yet',
    tryDifferentSearch: 'Try a different search term',
    uploadFirstFile: 'Upload your first file to get started',
    
    // Compliance Library Page  
    states: 'States',
    noStates: 'No states yet',
    constructionTypes: 'Construction Types',
    regulatoryEntities: 'Regulatory Entities',
    selectCategory: 'Select Category',
    selectCategoryDesc: 'Choose a state, construction type, and regulatory entity to view compliance files',
    uploadFirstComplianceFile: 'Upload your first compliance file to this category',
    copyToProject: 'Copy to Project...',
    
    // Sheet Index
    addSheet: 'Add Sheet',
    addNewSheet: 'Add New Sheet',
    sheetNumber: 'Sheet Number',
    sheetTitle: 'Sheet Title',
    intelligentPagination: 'Intelligent Auto-Pagination',
    paginationDescription: 'The system will automatically calculate how many pages each section needs. If content does not fit on one page, additional pages will be created.',
    selectSheetsToInclude: 'Select which sheets will be included in the final documentation',
    totalSheetsSelected: 'Total Sheets Selected',
    theseWillBeIncluded: 'These sheets will be included in your document',
    selectProjectTypeToSeeSheets: 'Select a project type to see available sheets',
    blocks: 'blocks',
    
    // Visual Editor
    visualDocumentBuilder: 'Visual Document Builder (Demo)',
    dragElementsToPosition: 'Drag elements to position them freely on your document canvas',
    preview: 'Preview',
    exportPDF: 'Export PDF',
    freeFormLayoutBuilder: 'Free-Form Layout Builder',
    visualEditorDescription: 'Click elements from the toolbar to add them to the canvas. Drag to position them anywhere. Each element shows real data from your project. This demonstrates a visual document builder where you can design custom cover pages and layouts before generating the final PDF.',
    addElements: 'ADD ELEMENTS',
    projectHeader: 'Project Header',
    clientInfo: 'Client Info',
    buildingSummaryLabel: 'Building Summary',
    rendersGallery: 'Renders Gallery',
    projectSchedule: 'Project Schedule',
    emptyCanvas: 'Empty Canvas',
    clickElementsToStart: 'Click elements from the toolbar above to start building',
    elementsOnCanvas: 'elements on canvas',
    dragToArrange: 'Drag them to arrange your custom document layout. Click Export PDF when ready.',
    documentArea: 'Document Area',
    
    // Toast Messages
    projectCreated: 'Project created successfully!',
    projectDeleted: 'Project deleted',
    projectDuplicated: 'Project duplicated',
    errorLoading: 'Error loading projects',
    errorDeleting: 'Error deleting project',
    errorDuplicating: 'Error duplicating project',
    errorCreating: 'Error creating project',
  },
  es: {
    // Dashboard
    dashboard: 'Panel de Control',
    projects: 'Proyectos',
    newProject: 'Nuevo Proyecto',
    allProjects: 'Todos los Proyectos',
    activeProjects: 'Proyectos Activos',
    completedProjects: 'Completados',
    draftProjects: 'Borradores',
    search: 'Buscar proyectos...',
    manageArchitecturalDocs: 'Gestiona la documentación de tus proyectos arquitectónicos',
    noProjectsFound: 'No se encontraron proyectos',
    startCreatingFirst: 'Comienza creando tu primer proyecto',
    loadingProjects: 'Cargando proyectos...',
    sheets: 'láminas',
    
    // Project Card
    edit: 'Editar',
    duplicate: 'Duplicar',
    delete: 'Eliminar',
    export: 'Exportar PDF',
    lastModified: 'Última modificación',
    
    // Project Types
    residential_remodel: 'Remodelación Residencial',
    residential_new: 'Nueva Construcción Residencial',
    tenant_improvement_food: 'Mejora de Inquilino - Food Service',
    tenant_improvement_retail: 'Mejora de Inquilino - Retail',
    tenant_improvement_office: 'Mejora de Inquilino - Oficina',
    commercial_new: 'Nueva Construcción Comercial',
    
    // Project Type Descriptions
    residentialRemodelDesc: 'Remodelación y ampliaciones residenciales',
    residentialNewDesc: 'Nueva construcción residencial',
    tenantFoodDesc: 'Espacios de restaurantes y servicio de alimentos',
    tenantRetailDesc: 'Espacios comerciales y retail',
    tenantOfficeDesc: 'Mejoras de inquilinos para oficinas',
    commercialNewDesc: 'Nueva construcción comercial',
    
    // Status
    draft: 'Borrador',
    in_progress: 'En Progreso',
    completed: 'Completado',
    archived: 'Archivado',
    
    // Wizard
    createNewProject: 'Crear Nuevo Proyecto',
    followSteps: 'Sigue los pasos para crear la documentación de tu proyecto',
    projectType: 'Tipo de Proyecto',
    basicInfo: 'Información Básica',
    buildingSummary: 'Resumen del Edificio',
    projectTeam: 'Equipo del Proyecto',
    scopeOfWork: 'Alcance del Trabajo',
    sheetIndex: 'Índice de Láminas',
    schedules: 'Tablas',
    compliance: 'Cumplimiento Normativo',
    rendersImages: 'Renders e Imágenes',
    review: 'Revisar y Exportar',
    
    // Project Type Step
    selectProjectType: 'Selecciona el tipo de proyecto que estás creando',
    pleaseSelectType: 'Por favor selecciona un tipo de proyecto para continuar',
    
    // Basic Info Step
    enterBasicInfo: 'Ingresa la información básica del proyecto',
    projectName: 'Nombre del Proyecto',
    projectNumber: 'Número de Proyecto',
    address: 'Dirección',
    city: 'Ciudad',
    state: 'Estado',
    zipCode: 'Código Postal',
    clientOwnerName: 'Nombre del Cliente/Propietario',
    architectName: 'Nombre del Arquitecto',
    scopeOfWorkLabel: 'Alcance del Trabajo',
    
    // Placeholders
    placeholderProjectName: 'ej., Hollywood Burbank Airport - Spring Chicken',
    placeholderProjectNumber: 'ej., 313-23/VPMMB',
    placeholderAddress: 'ej., 11445 Patom Dr.',
    placeholderCity: 'ej., Culver City',
    placeholderState: 'ej., CA',
    placeholderZip: 'ej., 90230',
    placeholderClient: 'ej., MCS Metropolitan Culinary Services',
    placeholderArchitect: 'ej., Sidley Jones Architects',
    placeholderScope: 'Describe el alcance del trabajo para este proyecto...',
    
    // Sheet Index Step
    selectSheetsToInclude: 'Selecciona qué láminas incluir en la documentación del proyecto',
    intelligentPagination: 'Auto-Paginación Inteligente',
    paginationDescription: 'El sistema calculará automáticamente cuántas páginas necesita cada sección. Si el contenido no cabe en una página, se crearán páginas adicionales (ej: G0.00, G0.01, G0.02).',
    selectProjectTypeToSeeSheets: 'Selecciona un tipo de proyecto para ver las láminas disponibles',
    totalSheetsSelected: 'Total de Láminas Seleccionadas',
    theseWillBeIncluded: 'Estas se incluirán en tu documentación',
    blocks: 'bloques',
    
    // Document Preview
    documentPreview: 'Vista Previa del Documento',
    autoPaginationPreview: 'Vista previa de auto-paginación',
    startFillingDetails: 'Comienza a llenar los detalles del proyecto para ver la vista previa',
    totalPages: 'Total de Páginas',
    warnings: 'advertencias',
    continued: 'continuado',
    contentWillContinue: 'El contenido continuará en',
    full: 'completo',
    
    // Buttons
    next: 'Siguiente',
    previous: 'Anterior',
    save: 'Guardar',
    cancel: 'Cancelar',
    finish: 'Finalizar',
    add: 'Agregar',
    access: 'Acceder',
    getStarted: 'Comenzar',
    browseLibrary: 'Explorar Biblioteca',
    
    // Landing Page
    landingTagline: 'Optimiza la documentación de tus proyectos arquitectónicos con organización inteligente y gestión de normativas',
    quickStats: 'Estadísticas Rápidas',
    comingSoon: 'Próximamente',
    
    // Sections
    projectFiles: 'Archivos de Proyecto',
    projectFilesDesc: 'Gestiona carpetas de proyectos con planos, documentos y archivos',
    projectFilesComingSoon: 'Sistema de gestión de archivos de proyecto con organización de carpetas, capacidades de carga/descarga y versionado de documentos.',
    
    complianceLibrary: 'Biblioteca de Normativas',
    complianceLibraryDesc: 'Accede a códigos de construcción y regulaciones por estado y tipo',
    complianceLibraryComingSoon: 'Biblioteca de normativas jerárquica organizada por estado, tipo de construcción y entidad reguladora con sugerencias inteligentes.',
    
    projectBuilder: 'Constructor de Proyectos',
    projectBuilderDesc: 'Crea y gestiona proyectos de documentación arquitectónica',
    
    // Project Files Page
    noProjects: 'No hay proyectos todavía',
    selectProject: 'Selecciona un Proyecto',
    selectProjectDesc: 'Elige un proyecto del lateral para ver y gestionar sus archivos',
    searchFiles: 'Buscar archivos...',
    uploading: 'Subiendo...',
    uploadFile: 'Subir Archivo',
    download: 'Descargar',
    noFilesFound: 'No se encontraron archivos',
    noFilesYet: 'No hay archivos todavía',
    tryDifferentSearch: 'Intenta con otro término de búsqueda',
    uploadFirstFile: 'Sube tu primer archivo para comenzar',
    
    // Compliance Library Page
    states: 'Estados',
    noStates: 'No hay estados todavía',
    constructionTypes: 'Tipos de Construcción',
    regulatoryEntities: 'Entidades Reguladoras',
    selectCategory: 'Selecciona Categoría',
    selectCategoryDesc: 'Elige un estado, tipo de construcción y entidad reguladora para ver archivos de normativas',
    uploadFirstComplianceFile: 'Sube tu primer archivo de normativa a esta categoría',
    copyToProject: 'Copiar a Proyecto...',
    
    // Sheet Index
    addSheet: 'Agregar Lámina',
    addNewSheet: 'Agregar Nueva Lámina',
    sheetNumber: 'Número de Lámina',
    sheetTitle: 'Título de Lámina',
    intelligentPagination: 'Auto-Paginación Inteligente',
    paginationDescription: 'El sistema calculará automáticamente cuántas páginas necesita cada sección. Si el contenido no cabe en una página, se crearán páginas adicionales.',
    selectSheetsToInclude: 'Selecciona qué láminas incluir en la documentación del proyecto',
    totalSheetsSelected: 'Total de Láminas Seleccionadas',
    theseWillBeIncluded: 'Estas se incluirán en tu documentación',
    selectProjectTypeToSeeSheets: 'Selecciona un tipo de proyecto para ver las láminas disponibles',
    blocks: 'bloques',
    
    // Visual Editor
    visualDocumentBuilder: 'Constructor Visual de Documentos (Demo)',
    dragElementsToPosition: 'Arrastra elementos para posicionarlos libremente en el canvas del documento',
    preview: 'Vista Previa',
    exportPDF: 'Exportar PDF',
    freeFormLayoutBuilder: 'Constructor de Diseño Libre',
    visualEditorDescription: 'Haz clic en los elementos de la barra de herramientas para agregarlos al canvas. Arrastra para posicionarlos donde quieras. Cada elemento muestra datos reales de tu proyecto. Esto demuestra un constructor visual de documentos donde puedes diseñar portadas y diseños personalizados antes de generar el PDF final.',
    addElements: 'AGREGAR ELEMENTOS',
    projectHeader: 'Encabezado del Proyecto',
    clientInfo: 'Info del Cliente',
    buildingSummaryLabel: 'Resumen del Edificio',
    rendersGallery: 'Galería de Renders',
    projectSchedule: 'Cronograma del Proyecto',
    emptyCanvas: 'Canvas Vacío',
    clickElementsToStart: 'Haz clic en elementos de la barra superior para comenzar a construir',
    elementsOnCanvas: 'elementos en el canvas',
    dragToArrange: 'Arrástralos para organizar tu diseño de documento personalizado. Haz clic en Exportar PDF cuando esté listo.',
    documentArea: 'Área del Documento',
    
    // Toast Messages
    projectCreated: '¡Proyecto creado exitosamente!',
    projectDeleted: 'Proyecto eliminado',
    projectDuplicated: 'Proyecto duplicado',
    errorLoading: 'Error al cargar proyectos',
    errorDeleting: 'Error al eliminar proyecto',
    errorDuplicating: 'Error al duplicar proyecto',
    errorCreating: 'Error al crear proyecto',
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  
  const t = (key) => {
    return translations[language][key] || key;
  };
  
  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };
  
  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};